import { ClientArchivedProjectRow, ClientProjectRow } from "@/data/models/ClientAdministrationRow";
import { matchesGroupedSelection, orderedSelection } from "@/utils/GroupedFilterSelection";

/**
 * The admin portal's project list: its grouped multi-filter (the shared rule in
 * `GroupedFilterSelection.ts`: any ticked option within a group, all groups with a tick across), its
 * search and its sort. Pure, so the rules are pinned by tests rather than by the screen.
 */
export enum ProjectListFilterGroup {
  STATUS = "STATUS",
  KIND = "KIND",
}

/**
 * The four lists of IRES Office, by its own names: Aktuelle, Fertige, Alte (archived) and Gelöschte
 * (archived and deleted). The last two come from the archive, which is loaded only when one of them
 * is ticked, or no status is (decision was made on 2026-10-01 (Taimoor)).
 */
export enum ProjectStatusOption {
  RUNNING = "RUNNING",
  FINISHED = "FINISHED",
  ARCHIVED = "ARCHIVED",
  DELETED = "DELETED",
}

export enum ProjectKindOption {
  DRYING = "DRYING",
  FLEX = "FLEX",
  LEAK_DETECTION = "LEAK_DETECTION",
}

/** One tickable option. The two vocabularies use distinct values, so one array holds a selection. */
export type ProjectListOption = ProjectStatusOption | ProjectKindOption;

/** Every option in menu order, with its group. */
export const PROJECT_LIST_OPTIONS: Array<{ option: ProjectListOption; group: ProjectListFilterGroup }> = [
  ...Object.values(ProjectStatusOption).map((option) => ({ option, group: ProjectListFilterGroup.STATUS })),
  ...Object.values(ProjectKindOption).map((option) => ({ option, group: ProjectListFilterGroup.KIND })),
];

export function projectListOptionGroup(option: ProjectListOption): ProjectListFilterGroup | null {
  return PROJECT_LIST_OPTIONS.find((entry) => entry.option === option)?.group ?? null;
}

const KIND_OF_OPTION: Record<ProjectKindOption, ClientProjectRow["kind"]> = {
  [ProjectKindOption.DRYING]: "drying",
  [ProjectKindOption.FLEX]: "flex",
  [ProjectKindOption.LEAK_DETECTION]: "leakDetection",
};

/** What the list opens with: the running and finished projects, so the archive is never loaded unasked. */
export const DEFAULT_PROJECT_LIST_SELECTION: ProjectListOption[] = [ProjectStatusOption.RUNNING, ProjectStatusOption.FINISHED];

/** Whether the selection is the default one, so the header need not name it. */
export function isDefaultProjectListSelection(selection: ProjectListOption[]): boolean {
  return (
    selection.length === DEFAULT_PROJECT_LIST_SELECTION.length &&
    DEFAULT_PROJECT_LIST_SELECTION.every((option) => selection.includes(option))
  );
}

/**
 * Whether the selection can show archived projects, so the archive has to be loaded: Alte or
 * Gelöschte ticked, or no status ticked at all (no ticked status means every status).
 */
export function selectionNeedsArchive(selection: ProjectListOption[]): boolean {
  const statuses = selection.filter((option) => projectListOptionGroup(option) === ProjectListFilterGroup.STATUS);
  return (
    statuses.length === 0 ||
    statuses.includes(ProjectStatusOption.ARCHIVED) ||
    statuses.includes(ProjectStatusOption.DELETED)
  );
}

/** One ticked option on its own. Status is the collection the row came from, as Cloud reports it. */
function matchesOption(row: ClientProjectRow, option: ProjectListOption): boolean {
  switch (option) {
    case ProjectStatusOption.RUNNING:
      return !row.finished;
    case ProjectStatusOption.FINISHED:
      return row.finished && !row.archived;
    case ProjectStatusOption.ARCHIVED:
      return row.archived && !row.deleted;
    case ProjectStatusOption.DELETED:
      return row.archived && row.deleted;
    default:
      return row.kind === KIND_OF_OPTION[option];
  }
}

/** Whether a project passes the ticked options: any within a group, all groups with a tick. */
export function matchesProjectListSelection(row: ClientProjectRow, selection: ProjectListOption[]): boolean {
  return matchesGroupedSelection(row, selection, projectListOptionGroup, matchesOption);
}

/**
 * A case-insensitive substring of the project name, either project number or the Sammelnummer: the
 * values someone on the phone reads out. An empty or blank query matches everything.
 */
export function matchesProjectSearch(row: ClientProjectRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();

  if (needle === "") {
    return true;
  }

  return [row.projectName, row.projectNumber, row.projectNumberIres, row.projectNameErp].some(
    (value) => value !== null && value.toLocaleLowerCase().includes(needle)
  );
}

/** The sort options, worded as IRES Office's own project list words them. */
export enum ProjectListSort {
  NEWEST = "NEWEST",
  OLDEST = "OLDEST",
  PROJECT_NUMBER = "PROJECT_NUMBER",
}

export const PROJECT_LIST_SORTS: ProjectListSort[] = Object.values(ProjectListSort);

/** By start, newest first; a project without a start date last; ties by id so the order is stable. */
function byNewest(a: ClientProjectRow, b: ClientProjectRow): number {
  return (b.startMillis ?? -Infinity) - (a.startMillis ?? -Infinity) || a.id.localeCompare(b.id);
}

/**
 * Filter, search and sort; the input is not modified. Oldest puts projects without a start date
 * last as well. Project number is the IRES number in natural order ("KIWM2" before "KIWM10"),
 * projects without one last, newest first among equals.
 */
export function selectProjectListRows(
  rows: ClientProjectRow[],
  selection: ProjectListOption[],
  query: string,
  sort: ProjectListSort
): ClientProjectRow[] {
  const selected = rows.filter((row) => matchesProjectListSelection(row, selection) && matchesProjectSearch(row, query));

  return selected.sort((a, b) => {
    if (sort === ProjectListSort.OLDEST) {
      return (a.startMillis ?? Infinity) - (b.startMillis ?? Infinity) || a.id.localeCompare(b.id);
    }

    if (sort === ProjectListSort.PROJECT_NUMBER) {
      if (a.projectNumberIres === null || b.projectNumberIres === null) {
        return Number(a.projectNumberIres === null) - Number(b.projectNumberIres === null) || byNewest(a, b);
      }
      return a.projectNumberIres.localeCompare(b.projectNumberIres, "de", { numeric: true }) || byNewest(a, b);
    }

    return byNewest(a, b);
  });
}

/** The ticked options in menu order, for the header ("Projekte: 12 (Aktuelle, Trocknung)"). */
export function orderedProjectListSelection(selection: ProjectListOption[]): ProjectListOption[] {
  return orderedSelection(selection, PROJECT_LIST_OPTIONS.map((entry) => entry.option));
}

/**
 * The i18n key of a finished project's end state, with the same labels as IRES Office's own project
 * rows (`SimpleProjectOverview`); null for a running project. An unknown or absent state reads as
 * "Abgeschlossen", as it does there.
 */
export function projectEndStateKey(row: ClientProjectRow): string | null {
  if (!row.finished) {
    return null;
  }

  if (row.deleted) {
    return "common.deleted";
  }

  switch (row.conclusionState) {
    case "ARCHIVED":
      return "common.archived";
    case "DELETED":
      return "common.deleted";
    case "MOVED_TO_FINISHED":
      return "common.movedToFinished";
    case "RESTORED":
      return "common.restored";
    default:
      return "common.concluded";
  }
}

/**
 * The last-visited key of a project row: the Projekte list tints the project whose reports were
 * opened last. Per client, at the level below the Mandanten list's, so opening another client clears it.
 */
export function clientProjectListLastVisitedKey(clientId: string, projectId: string): string {
  return `ADMIN_CLIENT_PROJECTS/${clientId}/${projectId}`;
}

/**
 * The archive after another slice arrives. A project on two pages keeps the entry from the higher
 * page, IRES Office's merge rule; the two pages can fall into different slices, so it is applied
 * across them here (within a slice IRES Cloud already did).
 */
export function mergeArchivedProjectSlice(
  archived: ClientArchivedProjectRow[],
  slice: ClientArchivedProjectRow[]
): ClientArchivedProjectRow[] {
  const byId = new Map<string, ClientArchivedProjectRow>();
  archived.forEach((row) => byId.set(row.id, row));

  for (const row of slice) {
    const existing = byId.get(row.id);
    if (!existing || row.pageNumber > existing.pageNumber) {
      byId.set(row.id, row);
    }
  }

  return Array.from(byId.values());
}

/**
 * The running and finished projects plus the archived ones. A project also listed as running or
 * finished is not listed again from the archive: a restore can leave a stale archived entry behind,
 * and the running or finished entry is the current one.
 */
export function combineWithArchivedProjects(
  current: ClientProjectRow[],
  archived: ClientProjectRow[]
): ClientProjectRow[] {
  const currentIds = new Set(current.map((row) => row.id));
  return [...current, ...archived.filter((row) => !currentIds.has(row.id))];
}

/** The selection the detail's Alte Projekte count opens with: every project its counter counts. */
export const ARCHIVE_PROJECT_LIST_SELECTION: ProjectListOption[] = [ProjectStatusOption.ARCHIVED, ProjectStatusOption.DELETED];

/** A preset selection as one route query value (`filters=ARCHIVED,DELETED`), and back; unknown values dropped. */
export function projectListSelectionToQuery(selection: ProjectListOption[]): Record<string, string> {
  return selection.length > 0 ? { filters: orderedProjectListSelection(selection).join(",") } : {};
}

/** The preset from the query, or null when there is none (the list then opens with its default). */
export function projectListSelectionFromQuery(query: Record<string, unknown>): ProjectListOption[] | null {
  if (typeof query.filters !== "string") {
    return null;
  }

  const known = PROJECT_LIST_OPTIONS.map((entry) => entry.option as string);
  const selection = query.filters.split(",").filter((value) => known.includes(value)) as ProjectListOption[];
  return selection.length > 0 ? selection : null;
}
